const authPath = '/authentication'
const authLoginPath = `${authPath}/login`
const authLoginPasswordPath = `${authLoginPath}/password`
const authLoginEmailPath = `${authLoginPath}/email`
const workspacesPath = '/workspaces'
const legacyOrganizationsPath = '/organizations'
const profilePath = '/profile'
const accountPath = '/account'

const normalizePathname = (pathname: string) =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname

export const authLoginPaths = [
  authLoginPath,
  authLoginPasswordPath,
  authLoginEmailPath
] as const

export const isAuthLoginPath = (pathname: string) =>
  authLoginPaths.includes(
    normalizePathname(pathname) as (typeof authLoginPaths)[number]
  )

const normalizeWorkspaceSection = (section?: string) => {
  switch (section) {
    case 'overview':
    case 'members':
    case 'organization':
    case 'roles':
    case 'security':
    case 'sso-scim':
    case 'domains':
    case 'public-access':
    case 'ai-connectors':
    case 'billing':
    case 'audit':
    case 'graph':
    case 'apps':
    case 'agents':
      return section
    case 'users':
      return 'members'
    case 'settings':
      return 'settings'
    default:
      return 'overview'
  }
}

const workspaceBase = (id: string) => `${workspacesPath}/${id}`
const workspaceSection = (id: string, section?: string) =>
  `${workspaceBase(id)}/${normalizeWorkspaceSection(section)}`
const workspaceAppBase = (workspaceId: string) =>
  `${workspaceBase(workspaceId)}/apps`
const workspaceAppDetailsBase = (workspaceId: string, appId: string) =>
  `${workspaceAppBase(workspaceId)}/${appId}`

const workspaceMembersBase = (workspaceId: string) =>
  `${workspaceBase(workspaceId)}/members`

export const paths = {
  /** Post-login index. `DefaultLandingRedirect` routes to the active workspace home. */
  home: '/',

  workspaces: {
    root: workspacesPath,
    new: `${workspacesPath}/new`,
    /** Post-login chooser shown when the user has >1 workspace and no remembered one. */
    select: `${workspacesPath}/select`,
    /** Post-login landing inside the workspace shell. */
    home: (id: string) => `${workspaceBase(id)}/home`,
    details: (id: string) => workspaceSection(id, 'overview'),
    detailsTab: (id: string, tab: string) => workspaceSection(id, tab),
    overview: (id: string) => workspaceSection(id, 'overview'),
    members: (id: string) => workspaceSection(id, 'members'),
    membersGroups: (id: string) => `${workspaceMembersBase(id)}/groups`,
    membersJoinRequests: (id: string) =>
      `${workspaceMembersBase(id)}/join-requests`,
    organization: (id: string) => workspaceSection(id, 'organization'),
    membersDivision: (workspaceId: string, divisionId: string) =>
      `${workspaceMembersBase(workspaceId)}/divisions/${divisionId}`,
    membersDepartment: (workspaceId: string, departmentId: string) =>
      `${workspaceMembersBase(workspaceId)}/departments/${departmentId}`,
    membersTeam: (workspaceId: string, teamId: string) =>
      `${workspaceMembersBase(workspaceId)}/teams/${teamId}`,
    membersPerson: (workspaceId: string, memberId: string) =>
      `${workspaceMembersBase(workspaceId)}/people/${memberId}`,
    roles: (id: string) => workspaceSection(id, 'roles'),
    security: (id: string) => workspaceSection(id, 'security'),
    settings: (id: string) => workspaceSection(id, 'settings'),
    ssoScim: (id: string) => `${workspaceSection(id, 'security')}?tab=ssoScim`,
    domains: (id: string) => workspaceSection(id, 'domains'),
    publicAccess: (id: string) => workspaceSection(id, 'public-access'),
    publicAccessDetail: (id: string, credentialId: string) =>
      `${workspaceSection(id, 'public-access')}/${credentialId}`,
    aiConnectors: (id: string) => workspaceSection(id, 'ai-connectors'),
    billing: (id: string) => workspaceSection(id, 'billing'),
    audit: (id: string) => workspaceSection(id, 'audit'),
    auditExports: (id: string) =>
      `${workspaceSection(id, 'audit')}?tab=exports`,
    auditLogDetails: (id: string, auditLogId: string) =>
      `${workspaceSection(id, 'audit')}/${auditLogId}`,
    companyGraph: (id: string) => workspaceSection(id, 'graph'),
    agents: (id: string) => workspaceSection(id, 'agents'),
    agentNew: (id: string) => `${workspaceSection(id, 'agents')}/new`,
    agentDetail: (id: string, agentId: string) =>
      `${workspaceSection(id, 'agents')}/${agentId}`,
    agentEdit: (id: string, agentId: string) =>
      `${workspaceSection(id, 'agents')}/${agentId}/edit`,
    apps: {
      root: (workspaceId: string) => workspaceSection(workspaceId, 'apps'),
      new: (workspaceId: string) => `${workspaceAppBase(workspaceId)}/new`,
      details: (workspaceId: string, appId: string) =>
        workspaceAppDetailsBase(workspaceId, appId),
      // Canonical section URLs for the app detail sidebar. The old `/roles`,
      // `/credentials`, `/webhooks`, `/audit` paths still resolve via
      // WorkspaceAppLegacyTabRedirect so existing bookmarks keep working.
      settings: (workspaceId: string, appId: string) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}?section=settings`,
      roles: (workspaceId: string, appId: string) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}?section=roles`,
      credentials: (workspaceId: string, appId: string) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}?section=credentials`,
      credentialDetail: (
        workspaceId: string,
        appId: string,
        credentialId: string
      ) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}/credentials/${credentialId}`,
      webhooks: (workspaceId: string, appId: string) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}?section=webhooks`,
      outboundWebhooks: (workspaceId: string, appId: string) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}?section=outboundWebhooks`,
      outboundWebhookDetail: (
        workspaceId: string,
        appId: string,
        webhookId: string
      ) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}/outbound-webhooks/${webhookId}`,
      audit: (workspaceId: string, appId: string) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}?section=activity`,
      auditExports: (workspaceId: string, appId: string) =>
        `${workspaceAppDetailsBase(workspaceId, appId)}?section=activity&audit_tab=exports`,
      auditLogDetails: (
        workspaceId: string,
        appId: string,
        auditLogId: string
      ) => `${workspaceAppDetailsBase(workspaceId, appId)}/audit/${auditLogId}`
    }
  },

  /** @deprecated Bookmark compatibility — prefer `paths.workspaces`. */
  legacyOrganizations: {
    root: legacyOrganizationsPath,
    details: (id: string) => `${legacyOrganizationsPath}/${id}`,
    detailsTab: (id: string, tab: string) =>
      `${legacyOrganizationsPath}/${id}/${tab}`
  },

  auth: {
    login: authLoginPath,
    loginPassword: authLoginPasswordPath,
    loginEmail: authLoginEmailPath,
    register: `${authPath}/register`,
    confirmRegister: `${authPath}/confirm-register`,
    resetPassword: `${authPath}/reset-password`,
    resetPasswordConfirm: `${authPath}/reset-password-confirm`,
    organizations: `${authPath}/organizations`,
    invitation: `${authPath}/invitation`,
    otp: `${authPath}/otp`,
    emailOtpVerify: `${authPath}/email-otp-verify`,
    emailOtpLink: `${authPath}/email-otp-link`,
    confirmMfaOtp: `${authPath}/confirm-mfa-otp`,
    confirmMfaBackupCode: `${authPath}/confirm-mfa-backup-code`,
    onboarding: `${authPath}/onboarding`,
    googleCallback: `${authPath}/google/callback`,
    googleConnectCallback: `${authPath}/google/connect-callback`,
    googleClientCallback: `${authPath}/google/client-callback`,
    googleClientConnectCallback: `${authPath}/google/client-connect-callback`,
    entraIdConnectCallback: `${authPath}/entraid/connect-callback`,
    entraIdOrgConnectCallback: `${authPath}/entraid/org-connect-callback`,
    appleCallback: `${authPath}/apple/callback`,
    appleClientCallback: `${authPath}/apple/client-callback`,
    appleClientConnectCallback: `${authPath}/apple/client-connect-callback`,
    appleConnectCallback: `${authPath}/apple/connect-callback`,
    appleOrgConnectCallback: `${authPath}/apple/org-connect-callback`
  },
  termsAndConditions: '/terms-and-conditions',
  billing: '/billing',

  /** Standalone MCP consent screen Core redirects AI clients' users to. */
  connect: {
    consent: (requestId: string) => `/connect/consent/${requestId}`
  },

  /**
   * Canonical Account paths. `AccountPage` maps the trailing `:tab` segment to a
   * tab; the legacy `authentication`, `audit` and `sessions` segments redirect to
   * the merged `security` / `activity` tabs.
   */
  account: {
    root: accountPath,
    profile: `${accountPath}/profile`,
    access: `${accountPath}/access`,
    authentication: `${accountPath}/authentication`,
    security: `${accountPath}/security`,
    notifications: `${accountPath}/notifications`,
    activity: `${accountPath}/activity`,
    audit: `${accountPath}/audit`,
    sessions: `${accountPath}/sessions`
  },

  /** @deprecated Bookmark compatibility — prefer `paths.account`. */
  profile: {
    root: (userId: string) => `${profilePath}/${userId}`,
    personal: (userId: string) => `${profilePath}/${userId}/personal`,
    authentication: (userId: string) =>
      `${profilePath}/${userId}/authentication`,
    security: (userId: string) => `${profilePath}/${userId}/security`,
    connectedAccounts: (userId: string) =>
      `${profilePath}/${userId}/connected-accounts`,
    notifications: (userId: string) => `${profilePath}/${userId}/notifications`,
    sessions: (userId: string) => `${profilePath}/${userId}/sessions`,
    audit: (userId: string) => `${profilePath}/${userId}/audit`
  }
}
